import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { getCompanyById, createCompany, updateCompany } from '../../services/companiesService';
import { useSupabaseClient } from '../../lib/supabase';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Building2, Package, FileText, Save, ShieldCheck, CreditCard, Send } from 'lucide-react';
import formatters from '../../styles/tokens/formatters';
import PspCredentialsSection from './PspCredentialsSection';

// Métricas fijas de Trust Partner. `label` es la glosa que se muestra en la landing
// (bajo el número); `title` es solo para el formulario en tadmin.
const TRUST_PARTNER_FIELDS = [
  { key: 'years',    title: 'Años en seguros',         label: 'años en seguros',        placeholder: 'ej: 65',       help: 'Años de trayectoria de la compañía en el mercado.' },
  { key: 'insured',  title: 'Asegurados activos',      label: 'asegurados activos',     placeholder: 'ej: 500.000+', help: 'Cantidad de asegurados o clientes vigentes.' },
  { key: 'presence', title: 'Países de operación',     label: 'países de operación',    placeholder: 'ej: 3',        help: 'Número de países donde opera el grupo.' },
  { key: 'branches', title: 'Sucursales',              label: 'sucursales',             placeholder: 'ej: 120',      help: 'Número de oficinas o puntos de atención.' },
  { key: 'rating',   title: 'Clasificación de riesgo', label: 'clasificación de riesgo', placeholder: 'ej: AA-',     help: 'Rating de clasificadoras (Feller Rate, Humphreys, etc.).' },
];

const normLabel = (v) => (v ?? '').toString().trim().toLowerCase();

const normalizeTrustPartner = (raw) => {
  let data = raw;
  if (typeof raw === 'string') {
    try { data = JSON.parse(raw); } catch { return { stats: [] }; }
  }
  const stats = Array.isArray(data?.stats) ? data.stats : [];
  return { stats: stats.map((s) => ({ value: s?.value ?? '', label: s?.label ?? '' })) };
};

const CompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [company, setCompany] = useState({
    nombre_corto: '',
    rut: '',
    razon_social: '',
    direccion: '',
    telefono: '',
    correo: '',
    sitio_web: '',
    id_alpo: '',
    numero: '',
    oficina: '',
    comuna: '',
    region: '',
    logo_url: '',
    trust_partner_data: { stats: [] },
    pago_propio: { usa_propio: false, proveedor: 'virtualpos' },
    config_emision: { metodo_entrega: 'manual' }
  });

  const PROVEEDORES_PAGO_PROPIO = [
    { value: 'toku',       label: 'Toku (PAC/PAT)' },
    { value: 'flow',       label: 'Flow (Tarjeta / TEF / PAC)' },
    { value: 'virtualpos', label: 'VirtualPOS' },
  ];
  
  const supabaseClient = useSupabaseClient();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      loadCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const data = await getCompanyById(supabaseClient, id);
      setCompany({
        ...data,
        trust_partner_data: normalizeTrustPartner(data?.trust_partner_data),
        pago_propio: {
          usa_propio: data?.pago_propio?.usa_propio ?? false,
          proveedor: data?.pago_propio?.proveedor ?? 'virtualpos',
        },
        config_emision: {
          metodo_entrega: data?.config_emision?.metodo_entrega ?? 'manual',
          ...data?.config_emision,
        },
      });
    } catch (error) {
      console.error('Error al cargar compañía:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      setSaving(true);
      const currentStats = company.trust_partner_data?.stats ?? [];
      const cleanStats = TRUST_PARTNER_FIELDS
        .map((f) => {
          const found = currentStats.find((s) => normLabel(s.label) === normLabel(f.label));
          const value = (found?.value ?? '').trim();
          return value ? { value, label: f.label } : null;
        })
        .filter(Boolean);
      // `producto` es una relación que se trae en el SELECT (no es columna de
      // `companias`); hay que excluirla del payload o Supabase rechaza el update.
      const { producto, ...companyColumns } = company;
      const payload = Object.fromEntries(
        Object.entries(companyColumns).map(([k, v]) => [k, v === '' ? null : v])
      );
      payload.trust_partner_data = cleanStats.length > 0 ? { stats: cleanStats } : null;
      // config_emision: limpiar vacíos; si solo queda metodo 'manual', guardar null
      const ce = { ...(company.config_emision ?? {}) };
      Object.keys(ce).forEach((k) => { if (ce[k] === '' || ce[k] == null) delete ce[k]; });
      if (ce.sftp_port) ce.sftp_port = Number(ce.sftp_port) || 22;
      payload.config_emision =
        !ce.metodo_entrega || (ce.metodo_entrega === 'manual' && Object.keys(ce).length === 1)
          ? null
          : ce;
      if (isEdit) {
        await updateCompany(supabaseClient, id, payload);
      } else {
        await createCompany(supabaseClient, payload);
      }
      navigate('/companies');
    } catch (err) {
      console.error('Error al guardar compañía:', err);
      setError(err?.message || 'Error al guardar la compañía');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
  };

  const setCE = (field, value) =>
    setCompany(prev => ({
      ...prev,
      config_emision: { ...prev.config_emision, [field]: value },
    }));

  const metodoEntrega = company.config_emision?.metodo_entrega ?? 'manual';

  const stats = company.trust_partner_data?.stats ?? [];

  const getStatValue = (label) =>
    stats.find((s) => normLabel(s.label) === normLabel(label))?.value ?? '';

  const setStat = (label, value) =>
    setCompany(prev => {
      const current = prev.trust_partner_data?.stats ?? [];
      const others = current.filter((s) => normLabel(s.label) !== normLabel(label));
      const next = value.trim() ? [...others, { value, label }] : others;
      return { ...prev, trust_partner_data: { stats: next } };
    });

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate('/companies')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Editar Compañía' : 'Nueva Compañía'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? 'Modifica los datos de la compañía' : 'Crea una nueva compañía aseguradora'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Información Básica</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre_corto">Nombre Corto *</Label>
                <Input
                  id="nombre_corto"
                  value={company.nombre_corto}
                  onChange={(e) => handleChange('nombre_corto', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rut">RUT *</Label>
                <Input
                  id="rut"
                  value={company.rut}
                  onChange={(e) => handleChange('rut', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="razon_social">Razón Social</Label>
              <Input
                id="razon_social"
                value={company.razon_social}
                onChange={(e) => handleChange('razon_social', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Textarea
                id="direccion"
                value={company.direccion}
                onChange={(e) => handleChange('direccion', e.target.value)}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="comuna">Comuna</Label>
                <Input
                  id="comuna"
                  value={company.comuna}
                  onChange={(e) => handleChange('comuna', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Región</Label>
                <Input
                  id="region"
                  value={company.region}
                  onChange={(e) => handleChange('region', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={company.numero}
                  onChange={(e) => handleChange('numero', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Información de Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={company.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="correo">Email</Label>
                <Input
                  id="correo"
                  type="email"
                  value={company.correo}
                  onChange={(e) => handleChange('correo', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sitio_web">Sitio Web</Label>
                <Input
                  id="sitio_web"
                  value={company.sitio_web}
                  onChange={(e) => handleChange('sitio_web', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oficina">Oficina</Label>
                <Input
                  id="oficina"
                  value={company.oficina}
                  onChange={(e) => handleChange('oficina', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="id_alpo">ID ALPO</Label>
                <Input
                  id="id_alpo"
                  value={company.id_alpo}
                  onChange={(e) => handleChange('id_alpo', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo_url">URL Logo</Label>
                <Input
                  id="logo_url"
                  value={company.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShieldCheck className="h-5 w-5" />
              <span>Trust Partner</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Métricas de confianza mostradas en la landing. Completa solo las que apliquen
              a esta compañía; las vacías no se muestran.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TRUST_PARTNER_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={`tp-${f.key}`}>{f.title}</Label>
                  <Input
                    id={`tp-${f.key}`}
                    placeholder={f.placeholder}
                    value={getStatValue(f.label)}
                    onChange={(e) => setStat(f.label, e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{f.help}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <span>Medio de Pago Propio</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Cuando está activo, el flujo de contratación usa el medio de pago de la compañía
              (flujo <strong>divina</strong>) en lugar del flujo general de Yool.
            </p>

            <div className="flex items-center space-x-3">
              <Switch
                id="usa_propio"
                checked={company.pago_propio?.usa_propio ?? false}
                onCheckedChange={(checked) =>
                  setCompany(prev => ({
                    ...prev,
                    pago_propio: { ...prev.pago_propio, usa_propio: checked },
                  }))
                }
              />
              <Label htmlFor="usa_propio">
                {company.pago_propio?.usa_propio ? 'Activo — usa pago propio' : 'Inactivo — usa flujo general'}
              </Label>
            </div>

            {company.pago_propio?.usa_propio && (
              <div className="space-y-2">
                <Label htmlFor="proveedor_pago">Proveedor</Label>
                <Select
                  value={company.pago_propio?.proveedor ?? 'virtualpos'}
                  onValueChange={(value) =>
                    setCompany(prev => ({
                      ...prev,
                      pago_propio: { ...prev.pago_propio, proveedor: value },
                    }))
                  }
                >
                  <SelectTrigger id="proveedor_pago" className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVEEDORES_PAGO_PROPIO.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {isEdit && company.pago_propio?.usa_propio && (
          <PspCredentialsSection
            companiaId={id}
            proveedor={company.pago_propio?.proveedor ?? 'virtualpos'}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Send className="h-5 w-5" />
              <span>Rendición a la compañía</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Cómo se entrega el archivo de rendición de pólizas generado en{' '}
              <strong>Emisión → Rendiciones</strong>. Con «Manual» el archivo solo queda
              disponible para descarga.
            </p>

            <div className="space-y-2">
              <Label htmlFor="ce_metodo">Método de entrega</Label>
              <Select value={metodoEntrega} onValueChange={(v) => setCE('metodo_entrega', v)}>
                <SelectTrigger id="ce_metodo" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual (descarga)</SelectItem>
                  <SelectItem value="email">Email (CSV adjunto)</SelectItem>
                  <SelectItem value="sftp">SFTP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {metodoEntrega === 'email' && (
              <div className="space-y-2">
                <Label htmlFor="ce_email">Email destino *</Label>
                <Input
                  id="ce_email"
                  type="email"
                  placeholder="rendiciones@aseguradora.cl"
                  value={company.config_emision?.email_destino ?? ''}
                  onChange={(e) => setCE('email_destino', e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Casilla de la compañía que recibe el CSV de rendición.
                </p>
              </div>
            )}

            {metodoEntrega === 'sftp' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ce_sftp_host">Host *</Label>
                  <Input
                    id="ce_sftp_host"
                    placeholder="sftp.aseguradora.cl"
                    value={company.config_emision?.sftp_host ?? ''}
                    onChange={(e) => setCE('sftp_host', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ce_sftp_port">Puerto</Label>
                  <Input
                    id="ce_sftp_port"
                    type="number"
                    placeholder="22"
                    value={company.config_emision?.sftp_port ?? ''}
                    onChange={(e) => setCE('sftp_port', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ce_sftp_usuario">Usuario *</Label>
                  <Input
                    id="ce_sftp_usuario"
                    value={company.config_emision?.sftp_usuario ?? ''}
                    onChange={(e) => setCE('sftp_usuario', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ce_sftp_password">Contraseña *</Label>
                  <Input
                    id="ce_sftp_password"
                    type="password"
                    value={company.config_emision?.sftp_password_enc ?? ''}
                    onChange={(e) => setCE('sftp_password_enc', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="ce_sftp_dir">Directorio remoto</Label>
                  <Input
                    id="ce_sftp_dir"
                    placeholder="/rendiciones"
                    value={company.config_emision?.sftp_directorio ?? ''}
                    onChange={(e) => setCE('sftp_directorio', e.target.value)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isEdit && company.producto && company.producto.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Productos y Planes</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {company.producto.map((producto) => (
                  <div key={producto.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{producto.nombre}</h4>
                      <Badge variant={producto.activo ? 'default' : 'secondary'}>
                        {producto.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    {producto.yoolbrand && (
                      <p className="text-sm text-muted-foreground mb-2">
                        Marca: {producto.yoolbrand}
                      </p>
                    )}
                    {producto.descripcion && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {producto.descripcion}
                      </p>
                    )}
                    
                    {producto.planes && producto.planes.length > 0 && (
                      <div>
                        <Separator className="my-3" />
                        <div className="flex items-center space-x-2 mb-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Planes ({producto.planes.length})</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {producto.planes.map((plan) => (
                            <div key={plan.id} className="flex items-center justify-between p-2 bg-muted rounded">
                              <div>
                                <span className="text-sm font-medium">{plan.nombre}</span>
                                {plan.codigo_plan && (
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({plan.codigo_plan})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {plan.uf_base && (
                                  <span className="text-xs text-muted-foreground">
                                    {formatters.formatUF(plan.uf_base)}
                                  </span>
                                )}
                                <Badge variant={plan.activo ? 'default' : 'secondary'} className="text-xs">
                                  {plan.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={() => navigate('/companies')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;